/**
 * Zod parsers for WebSocket events.
 *
 * Used at the boundary by feature slices in ``apps/client`` to harden
 * against malformed BE payloads — protocol contract verified at parse time
 * before mutating Zustand state.
 *
 * Intentionally loose for fields whose internal shape varies per agent
 * (``payload``, ``sources``, ``metadata``, etc.) — feature slices refine
 * with narrower schemas at the call-site.
 */

import { z } from "zod";

import { DisplaySchema } from "../domain/displays.js";

// ---------------------------------------------------------------------------
// Heartbeat
// ---------------------------------------------------------------------------

export const WSPingEventSchema = z.object({ type: z.literal("ping") });
export const WSPongEventSchema = z.object({
  type: z.literal("pong"),
  timestamp: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

export const WSChatMessageTypeSchema = z.enum([
  "start",
  "status",
  "text",
  // Jul 3, 2026 — SOTA text-part streaming via Pydantic-AI
  // ``event_stream_handler`` on the BE. One event per ``TextPartDelta`` the
  // model emits during answer synthesis, letting the FE render a
  // token-by-token typing UX. Payload contract: ``delta`` is the verbatim
  // delta text (`content` stays optional for backward-compat with plain
  // ``text`` streaming), ``part_index`` orders multi-part answers within
  // the SAME ``(conversation_id, query_id)`` tuple. ``id`` follows the
  // ``<query_id>:answer`` convention so ``appendStreamChunk`` accumulates
  // every delta into ONE assistant message.
  "answer_delta",
  // Jul 3, 2026 — SOTA extended-thinking streaming. Emitted BE-side
  // when the model produces a ``ThinkingPartDelta`` (Vertex Gemini 3.5
  // ``thinking_level`` ≥ LOW / OpenAI reasoning summaries / Anthropic
  // extended thinking). Each event carries a SANITIZED delta (tool_call
  // XML / user_facts / credentials pre-scrubbed BE-side via
  // ``_sanitize_thinking_text``). Same wire shape as ``answer_delta``
  // (``delta``, ``part_index``, id ``<query_id>:thinking``); the FE
  // handler funnels the delta into the reasoning slice, which drives
  // ``ThinkingChain`` live-thinking rendering + the collapsable
  // "Ragionamento" chip that persists after the answer lands.
  "thinking_delta",
  "answer",
  "result",
  "complete",
  "completed",
  "stopped",
  "error",
  "warning",
  "title",
  "ner",
  "decision",
  "suggestion",
  "user_prompt",
  "tree_update",
  "training_update",
  "self_healing_error",
  "rate_limit_error",
  "authentication_error",
  "billing_error",
  "tree_timeout_error",
  "user_timeout_error",
  "reasoning_hop",
  "query_state_replay",
]);

export const WSChatEventSchema = z.object({
  type: WSChatMessageTypeSchema,
  id: z.string(),
  user_id: z.string(),
  conversation_id: z.string(),
  query_id: z.string(),
  timestamp: z.string().optional(),
  payload: z.unknown().optional(),
  content: z.string().optional(),
  // Jul 3, 2026 — token-level streaming for ``answer_delta`` AND
  // ``thinking_delta`` events. Kept OPTIONAL so every other event type
  // parses unchanged. ``wsHandlers`` reads from ``delta`` for the two
  // streaming events and from ``content`` for the legacy ``text`` stream.
  // The ``type`` discriminator determines whether the delta lands in the
  // answer bubble (``appendStreamChunk``) or in the reasoning slice
  // (``appendThinkingDelta`` → ``ThinkingChain`` live surface).
  delta: z.string().optional(),
  part_index: z.number().optional(),
  sources: z.array(z.unknown()).optional(),
  raw_context: z.array(z.unknown()).optional(),
  confidence_score: z.number().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  // PR-A1 (May 26, 2026): structured-data displays are parsed at the WS
  // boundary so the chat renderer doesn't have to re-validate every
  // frame. The union schema warns-and-keeps for unknown ``source_type``
  // variants so the FE never hard-fails on a forward-compat BE deploy.
  displays: z.array(DisplaySchema).optional(),
  provenance_map: z.unknown().optional(),
  action_proposal: z.unknown().optional(),
  image_attachments: z.array(z.unknown()).optional(),
  file_attachments: z.array(z.unknown()).optional(),
  error: z.string().optional(),
  error_code: z.string().optional(),
  hop: z.unknown().optional(),
  is_final: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Canvas
// ---------------------------------------------------------------------------

export const CanvasCompilePhaseSchema = z.enum([
  "fetch_context",
  "plan_sections",
  "compile_sections",
  "resolve_cross_refs",
  "verify_claims",
  "persist",
  "done",
  "failed",
]);

export const CanvasCompileProgressEventSchema = z.object({
  type: z.literal("canvas_compile_progress"),
  canvas_id: z.string(),
  user_id: z.string(),
  phase: CanvasCompilePhaseSchema,
  pct: z.number(),
  status: z.enum(["compiling", "ready", "failed"]),
  confidence_score: z.number().nullable(),
  error_message: z.string().nullable(),
  timestamp: z.string(),
});

export const CanvasSectionCompleteEventSchema = z.object({
  type: z.literal("canvas_section_complete"),
  canvas_id: z.string(),
  user_id: z.string(),
  section: z.object({
    id: z.string(),
    title: z.string(),
    anchor: z.string(),
    level: z.number(),
    content_md: z.string(),
    citation_map_partial: z.record(
      z.string(),
      z.object({
        source_id: z.string(),
        claim_text: z.string().optional(),
        confidence: z.number().optional(),
      }),
    ),
    index: z.number(),
    total: z.number(),
  }),
  ts: z.string(),
});

// ---------------------------------------------------------------------------
// Nudge
// ---------------------------------------------------------------------------

export const NudgeTypeSchema = z.enum([
  "deadline_reminder",
  "forgotten_task",
  "pattern_break",
  "insight",
  "encouragement",
  "support",
]);

export const NudgeUrgencySchema = z.enum(["low", "normal", "high", "urgent"]);

export const NudgeReceivedPayloadSchema = z.object({
  nudge_id: z.string(),
  type: NudgeTypeSchema,
  urgency: NudgeUrgencySchema,
  title: z.string(),
  preview: z.string(),
  action_type: z.string().nullable(),
  action_payload: z.record(z.string(), z.unknown()),
  delivered_at: z.string(),
});

export const NudgeReceivedEventSchema = z.object({
  type: z.literal("nudge_received"),
  user_id: z.string(),
  conversation_id: z.string().optional(),
  query_id: z.string().optional(),
  timestamp: z.string(),
  payload: NudgeReceivedPayloadSchema,
});

// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------

export const CapabilitiesChangedEventSchema = z.object({
  type: z.literal("capabilities_changed"),
  user_id: z.string(),
  timestamp: z.string(),
  payload: z
    .object({
      changed_capabilities: z.array(z.string()).optional(),
    })
    .optional(),
});

// ---------------------------------------------------------------------------
// HITL
// ---------------------------------------------------------------------------

export const ActionProposalEventSchema = z.object({
  type: z.literal("action_proposal"),
  user_id: z.string(),
  conversation_id: z.string(),
  query_id: z.string(),
  proposal: z.unknown(),
  timestamp: z.string(),
});

export const ActionStatusEventSchema = z.object({
  type: z.literal("action_status"),
  conversation_id: z.string(),
  proposal_id: z.string(),
  status: z.string(),
  message: z.string().optional(),
  undo_available: z.boolean(),
  timestamp: z.string(),
  clarification_message: z.string().optional(),
  clarification_missing: z.array(z.string()).optional(),
  // Async document_create completion (Temporal path): the generated doc's
  // coordinates so the FE can auto-open it (the blocking HTTP body is gone).
  result_data: z
    .object({
      doc_id: z.string().nullish(),
      minio_bucket: z.string().nullish(),
      minio_key: z.string().nullish(),
      access_url: z.string().nullish(),
    })
    .passthrough()
    .optional(),
});

// Source confirmation card — see types.ts ``ActionSourceConfirmationEvent``
// for the full contract & FE flow. Zod parser pairs with that interface so
// the WSClient can validate untrusted payloads at the wire boundary.
const ActionSourceCandidateSchema = z.object({
  content_item_id: z.string(),
  weaviate_doc_id: z.string().nullable(),
  filename: z.string(),
  source: z.enum(["sidebar", "chat_attachment", "search_result"]),
  // Human storage-source label for name-resolved candidates ("Google Drive" /
  // "Caricato manualmente"). Optional + nullable so older BE payloads parse.
  source_label: z.string().nullable().optional(),
  confidence: z.number().nullable(),
  snippet: z.string().nullable(),
});

const ActionSourceConfirmationPendingActionSchema = z.object({
  action_type: z.literal("document_fork_edit"),
  source_document_title: z.string(),
  new_title: z.string().nullable(),
  description: z.string(),
  edits_count: z.number(),
});

export const ActionSourceConfirmationEventSchema = z.object({
  type: z.literal("action_source_confirmation"),
  user_id: z.string(),
  conversation_id: z.string(),
  query_id: z.string(),
  payload: z.object({
    confirmation_id: z.string(),
    candidates: z.array(ActionSourceCandidateSchema),
    preferred_content_item_id: z.string(),
    pending_action: ActionSourceConfirmationPendingActionSchema,
  }),
  timestamp: z.string(),
});

// Document edit proposal — May 2026 SOTA HITL bridge between the
// Pydantic AI agent and the in-app editors (DocxEditorInline /
// XlsxEditorInline). The agent emits this event whenever a chat-side
// tool would mutate a document the user has open; the FE applies the
// change as a *tracked* suggestion (accept/reject UX), never directly.
// See ``tasks/v2_editor_architecture.md`` (Ando agent repo) for the
// rationale: read-side tools BE-side, write-side application FE-side.
export const DocEditProposalEventSchema = z.object({
  type: z.literal("doc_edit_proposal"),
  /** Target document id — matches an open ``EditSession.doc_id``. */
  doc_id: z.string(),
  /** Opaque proposal id — used to ack accept / reject server-side later. */
  proposal_id: z.string(),
  /** Discriminates which editor handle method receives the payload. */
  kind: z.enum(["docx_replace_range", "xlsx_set_cell"]),
  /** Free-form payload validated FE-side per ``kind``. */
  payload: z.unknown(),
  /** Optional human-readable explanation surfaced in the accept/reject UX. */
  rationale: z.string().optional(),
  timestamp: z.string(),
});

// Doc diff payload — Phase 2.1 (May 2026). Cross-mime diff envelope
// emitted alongside ``doc_edit_proposal`` for proposals that
// materialise into a structured change list. Drives ``<ReviewRail>``.
// See types.ts ``DocDiffPayloadEvent`` for the full contract.
export const DocDiffChangeSchema = z.object({
  change_id: z.string(),
  kind: z.enum(["insertion", "deletion", "modification", "structural"]),
  summary: z.string(),
  rationale: z.string().optional(),
  payload: z.unknown(),
});

export const DocDiffPayloadSchema = z.object({
  proposal_id: z.string(),
  doc_id: z.string(),
  mime: z.enum(["docx", "xlsx", "pptx"]),
  changes: z.array(DocDiffChangeSchema),
  summary: z.string().optional(),
  redline_url: z.string().nullable().optional().transform((value) => value ?? undefined),
});

export const DocDiffPayloadEventSchema = z.object({
  type: z.literal("doc_diff_payload"),
  payload: DocDiffPayloadSchema,
  timestamp: z.string(),
});

// ---------------------------------------------------------------------------
// Sales prospecting — async run finished (discovery / deep-research / bulk send
// / email backfill). Lets the leads view drop the in-flight loader and toast a
// real outcome instead of guessing via a timeout.
// ---------------------------------------------------------------------------

export const ProspectingRunKindSchema = z.enum([
  "discovery",
  "deep_research",
  "bulk_send",
  "email_backfill",
]);

export const ProspectingRunDonePayloadSchema = z.object({
  campaign_id: z.string(),
  kind: ProspectingRunKindSchema,
  // Outcome fields — present per-kind (all optional so the schema is forward-
  // compatible if the BE adds more).
  mode: z.string().optional(),
  found: z.number().optional(),
  inserted: z.number().optional(),
  sent: z.number().optional(),
  skipped: z.number().optional(),
  // ``failed`` = bulk-send per-recipient failure COUNT (a number).
  failed: z.number().optional(),
  checked: z.number().optional(),
  filled: z.number().optional(),
  // ``run_failed`` = the whole run ultimately FAILED (retries exhausted / worker
  // death). Distinct from the ``failed`` count above; drives the "retry" toast.
  run_failed: z.boolean().optional(),
});

export const ProspectingRunDoneEventSchema = z.object({
  type: z.literal("prospecting_run_done"),
  user_id: z.string().optional(),
  timestamp: z.string().optional(),
  payload: ProspectingRunDonePayloadSchema,
});

// ---------------------------------------------------------------------------
// Banco room (Aug 31, 2026) — presence + field-locks
// ---------------------------------------------------------------------------

export const BancoRoomUserSchema = z.object({
  user_id: z.string(),
  name: z.string(),
  initials: z.string(),
  color: z.string(),
});

export const BancoPresenceEventSchema = z.object({
  type: z.literal("banco_presence"),
  user_id: z.string(),
  timestamp: z.string(),
  payload: z.object({ run_id: z.string(), users: z.array(BancoRoomUserSchema) }),
});

export const BancoLockEventSchema = z.object({
  type: z.literal("banco_lock"),
  user_id: z.string(),
  timestamp: z.string(),
  payload: BancoRoomUserSchema.extend({ run_id: z.string(), field: z.string() }),
});

export const BancoUnlockEventSchema = z.object({
  type: z.literal("banco_unlock"),
  user_id: z.string(),
  timestamp: z.string(),
  payload: z.object({ run_id: z.string(), field: z.string() }),
});

// ---------------------------------------------------------------------------
// Union
// ---------------------------------------------------------------------------

export const WSEventSchema = z.discriminatedUnion("type", [
  WSPingEventSchema,
  WSPongEventSchema,
  WSChatEventSchema,
  CanvasCompileProgressEventSchema,
  CanvasSectionCompleteEventSchema,
  NudgeReceivedEventSchema,
  CapabilitiesChangedEventSchema,
  ActionProposalEventSchema,
  ActionStatusEventSchema,
  ActionSourceConfirmationEventSchema,
  DocEditProposalEventSchema,
  DocDiffPayloadEventSchema,
  ProspectingRunDoneEventSchema,
  BancoPresenceEventSchema,
  BancoLockEventSchema,
  BancoUnlockEventSchema,
]);

export type ParsedWSEvent = z.infer<typeof WSEventSchema>;
export type ProspectingRunDoneEvent = z.infer<typeof ProspectingRunDoneEventSchema>;
export type DocEditProposalEvent = z.infer<typeof DocEditProposalEventSchema>;
export type ParsedDocDiffPayloadEvent = z.infer<typeof DocDiffPayloadEventSchema>;
export type ParsedDocDiffChange = z.infer<typeof DocDiffChangeSchema>;
